import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { ProjectModelWithRelations } from '@rappider/rappider-sdk';
import { getProjectModelsWithFields } from 'libs/shared/src/lib/state-selectors';
import { selectProjectModels } from 'libs/project/src/lib/states/project-model-state/project-model.selectors';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataFieldListComponent } from '../../../../../../project/src/lib/components/data-field-list/data-field-list.component';
import * as ProjectModelActions from 'libs/project/src/lib/states/project-model-state/project-model.actions';
import * as ProjectModelRelationActions from 'libs/project/src/lib/states/project-model-relation-state/project-model-relation.actions';
import { DataModelService } from '../../../services/data-model.service';

@Component({
  selector: 'rappider-ai-generate-data-model-canvas',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzToolTipModule,
    DataFieldListComponent
  ],
  templateUrl: './ai-generate-data-model-canvas.component.html',
  styleUrls: ['./ai-generate-data-model-canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiGenerateDataModelCanvasComponent implements OnInit, OnDestroy, OnChanges {

  @Input() projectModelId: string | undefined;

  subscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();

  projectModels: ProjectModelWithRelations[] | undefined;
  selectedProjectModel: ProjectModelWithRelations | undefined;
  isLoading = false;
  isUpdating = false;
  loadingMessage = '';
  updatingMessage = '';

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private actions$: Actions,
    private dataModelService: DataModelService
  ) {
    // Constructor intentionally left empty
  }

  ngOnInit() {
    this.subscribeToData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectModelId'] && changes['projectModelId'].currentValue) {
      this.selectProjectModel();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToRouteParameters(),
      ...this.subscribeToProjectModels(),
      ...this.subscribeToDataModelCreation(),
      this.subscribeToModelUpdateSuccess()
    ];

    this.subscribeToUpdateActions();
  }

  subscribeToRouteParameters() {
    return this.route.paramMap.subscribe((params: ParamMap) => {
      // Only get projectModelId from route if not provided as input
      if (!this.projectModelId) {
        this.projectModelId = params.get('projectModelId') ?? undefined;
      }
      this.selectProjectModel();
    });
  }

  subscribeToProjectModels() {
    // First subscribe to project models only (without fields dependency)
    const projectModelsSubscription = this.store.select(selectProjectModels)
      .subscribe((projectModels: unknown) => {
        const models = projectModels as ProjectModelWithRelations[] | undefined;
        if (models && models.length > 0) {
          this.projectModels = models;
          this.selectProjectModel();
        }
      });

    // Then subscribe to project models with fields for complete data
    const projectModelsWithFieldsSubscription = this.store.select(getProjectModelsWithFields).subscribe(projectModelsWithFields => {
      if (projectModelsWithFields && projectModelsWithFields.length > 0) {
        this.projectModels = projectModelsWithFields;
        this.selectProjectModel();
      }
    });

    return [projectModelsSubscription, projectModelsWithFieldsSubscription];
  }

  selectProjectModel() {
    if (this.projectModels?.length) {
      if (this.projectModelId) {
        // Specific model ID provided
        this.selectedProjectModel = this.projectModels?.find(pm => pm.id === this.projectModelId);
      } else {
        // No specific ID, select the latest model
        this.selectedProjectModel = [...this.projectModels]
          .sort((a, b) => new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime())[0];
      }
      this.cd.markForCheck();
    } else {
      this.selectedProjectModel = undefined;
      this.cd.markForCheck();
    }
  }

  subscribeToDataModelCreation() {
    // Listen to data model creation start (both old and new actions)
    const startSubscription = this.actions$.pipe(
      ofType(
        ProjectModelActions.ActionTypes.SaveGeneratedDataModelsWithAI,
        ProjectModelRelationActions.ActionTypes.GenerateDataModelsWithRelations
      ),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isLoading = true;
      this.loadingMessage = 'Creating data models...';
      this.cd.markForCheck();
    });

    // Listen to data model creation success (both old and new actions)
    const successSubscription = this.actions$.pipe(
      ofType(
        ProjectModelActions.ActionTypes.SaveGeneratedDataModelsWithAISuccessful,
        ProjectModelRelationActions.ActionTypes.GenerateDataModelsWithRelationsSuccessful
      ),
      takeUntil(this.destroy$)
    ).subscribe((action: unknown) => {
      this.isLoading = false;
      this.loadingMessage = '';

      const actionObj = action as Record<string, unknown>;
      const payload = actionObj.payload as Record<string, unknown>;
      if (payload?.savedDataModels || payload?.response) {
        this.handleDataModelCreated();
      }
    });

    // Listen to data model creation failure
    const failureSubscription = this.actions$.pipe(
      ofType(
        ProjectModelActions.ActionTypes.SaveGeneratedDataModelsWithAIFailure,
        ProjectModelRelationActions.ActionTypes.GenerateDataModelsWithRelationsFailure
      ),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isLoading = false;
      this.loadingMessage = '';
      this.cd.markForCheck();
    });

    return [startSubscription, successSubscription, failureSubscription];
  }

  /**
   * Subscribe to data model update events from DataModelService
   * Similar to how renderer tracks save events from AIChatGeneratorService
   * @private
   */
  private subscribeToUpdateActions(): void {
    this.dataModelService.subscribeToUpdateProgress()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isUpdating: boolean) => {
        this.isUpdating = isUpdating;
        this.updatingMessage = isUpdating ? 'Updating data model...' : '';
        this.cd.markForCheck();
      });
  }

  private handleDataModelCreated(): void {
    setTimeout(() => {
      this.loadLatestCreatedModel();
    }, 1000);
  }

  private loadLatestCreatedModel(): void {
    this.store.select(getProjectModelsWithFields)
      .pipe(takeUntil(this.destroy$))
      .subscribe(availableModelsWithFields => {
        if (availableModelsWithFields && availableModelsWithFields.length > 0) {
          this.selectLatestModel(availableModelsWithFields);
        } else {
          this.store.select(selectProjectModels)
            .pipe(takeUntil(this.destroy$))
            .subscribe((availableModels: unknown) => {
              const models = availableModels as ProjectModelWithRelations[] | undefined;
              if (models && models.length > 0) {
                this.selectLatestModel(models);
              } else {
                setTimeout(() => {
                  this.loadLatestCreatedModel();
                }, 1000);
              }
            });
        }
      });
  }

  private selectLatestModel(availableModels: ProjectModelWithRelations[]): void {
    const latestModel = [...availableModels]
      .sort((a, b) => new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime())[0];

    if (latestModel && latestModel.id !== this.selectedProjectModel?.id) {
      this.projectModelId = latestModel.id;
      this.selectedProjectModel = latestModel;
      this.cd.markForCheck();
    }
  }

  /**
   * Subscribe to model update success events
   */
  subscribeToModelUpdateSuccess() {
    const handleModelUpdateSuccess = (event: CustomEvent) => {
      const projectModelId = event.detail?.projectModelId;
      if (projectModelId) {
        // Store'u yeniden yükle
        this.store.dispatch(new ProjectModelActions.GetProjectModels());
        // Model seçimini güncelle
        setTimeout(() => {
          this.selectProjectModel();
        }, 1000);
      }
    };

    window.addEventListener('modelUpdateSuccess', handleModelUpdateSuccess as EventListener);

    return new Subscription(() => {
      window.removeEventListener('modelUpdateSuccess', handleModelUpdateSuccess as EventListener);
    });
  }

  onCanvasButtonClicked(buttonKey: unknown) {
    console.log('onCanvasButtonClicked ', buttonKey);
  }
}
